import { ScaleElement } from '@scale/base-element';

class ScaleComparison extends ScaleElement {
  #initialPosition = 50;

  setup() {
    const mode = this.getAttribute('data-mode');
    if (mode === 'slider') this.#setupSlider();
    if (mode === 'stacked') this.#setupStacked();
    if (mode === 'overlay') this.#setupOverlay();
  }

  #setupSlider() {
    const slider = this.ref('slider');
    const before = this.ref('before');
    const container = this.ref('container');
    if (!slider || !before || !container) return;

    this.#initialPosition = this.getNumber('data-position', 50);
    slider.value = this.#initialPosition;
    this.#sync(this.#initialPosition);

    slider.addEventListener('input', () => this.#sync(slider.value), { signal: this.signal });
  }

  #setupStacked() {
    if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
    if (this.getBoundingClientRect().top < window.innerHeight) return;

    this.setAttribute('data-stacked-enter', '');
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      this.removeAttribute('data-stacked-enter');
    }, { threshold: 0.3 });
    observer.observe(this);
    this.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  }

  #setupOverlay() {
    const reveal = this.ref('reveal');
    if (!reveal) return;

    let pointerType = '';
    const track = (event) => {
      if (event.pointerType === 'mouse') this.toggleAttribute('data-hovered', event.type === 'pointerenter');
    };
    reveal.addEventListener('pointerenter', track, { signal: this.signal });
    reveal.addEventListener('pointerleave', track, { signal: this.signal });
    reveal.addEventListener('pointerdown', (event) => { pointerType = event.pointerType; }, { signal: this.signal });

    reveal.addEventListener('click', () => {
      const fromMouse = pointerType === 'mouse';
      pointerType = '';
      if (fromMouse) return;
      const revealed = reveal.getAttribute('aria-pressed') !== 'true';
      reveal.setAttribute('aria-pressed', String(revealed));
      this.toggleAttribute('data-revealed', revealed);
    }, { signal: this.signal });
  }

  #sync(value) {
    const before = this.ref('before');
    const container = this.ref('container');
    if (!before || !container) return;

    const percent = parseFloat(value);
    container.style.setProperty('--compare', percent);
    before.style.clipPath = `inset(0 ${100 - percent}% 0 0)`;
  }

  onBlockSelect() {
    const slider = this.ref('slider');
    if (slider) {
      slider.value = this.#initialPosition;
      this.#sync(this.#initialPosition);
    }
  }
}

if (!customElements.get('scale-comparison')) {
  customElements.define('scale-comparison', ScaleComparison);
}
